<template>
  <div id="stakeholdersList">
    <!-- Activated Stakeholders -->
    <el-table
      v-loading="loading"
      class="clear-table mainCard stakeholderTable defaultCursor"
      :data="formattedStakeholders"
      :row-class-name="rowClass"
      :empty-text="$t('leader.coworker.noData')"
    >
      <el-table-column
        prop="org_relationship"
        :label="$t('leader.coworker.relationship')"
        class-name="breakoutGroup"
        min-width="100"
      >
        <template slot-scope="scope">
          <span v-if="!scope.row.hide_relationship">
            {{ scope.row.org_relationship ?
              $t(`stakeholderGroup.${scope.row.org_relationship}`) :
              ''
            }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('common.name')"
        min-width="100"
      >
        <template slot-scope="scope">
          <div class="nameWrap">
            {{ scope.row.first_name }} {{ scope.row.last_name }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="email"
        :label="$t('common.email')"
        min-width="150"
      />
      <el-table-column
        min-width="90"
        align="right"
      >
        <template slot-scope="scope">
          <el-button
            class="clearButton"
            type="plain"
            round
            style="margin-right: 20px;"
            @click="toggleEdit(scope.row)"
          >
            {{ $t('common.edit') }}
            <i class="el-icon-edit"></i>
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Deactivated Stakeholders -->
    <el-card
      v-if="deactivatedStakeholders.length > 0"
      class="mainCard clear-card deactivatedStakeholders"
      shadow="hover"
      style="margin-top: 18px;"
      :body-style="{padding: '30px'}"
    >
      <div
        class="groupTitle"
        style="padding: 0 0 13px; font-weight: bold;"
      >
        {{ $t('leader.coworker.deactivated') }}
      </div>

      <div
        v-for="(stakeholder, sKey) in deactivatedStakeholders"
        :key="sKey"
        class="currentStakeholder"
        style="padding: 6px 0;"
      >
        <el-row
          :gutter="10"
          type="flex"
          align="middle"
          justify="center"
        >
          <el-col :span="5">
            {{ stakeholder.first_name }}
          </el-col>
          <el-col :span="6">
            {{ stakeholder.last_name }}
          </el-col>
          <el-col :span="8">
            {{ stakeholder.email }}
          </el-col>
          <el-col
            :span="5"
            style="text-align: right;"
          >
            <el-button
              class="editButton clearButton"
              type="plain"
              round
              @click="toggleEdit(stakeholder)"
            >
              {{ $t('common.edit') }} <i class="el-icon-edit"></i>
            </el-button>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <div class="actions mainCard">
      <el-tooltip
        v-if="!isConfirmed"
        class="item"
        effect="dark"
        :content="$t('leader.coworker.publishTooltip')"
        placement="top"
        :disabled="!(isConfirmed || session < 3)"
      >
        <div>
          <el-button
            type="primary"
            round
            :disabled="isConfirmed || session < 3"
            :class="isConfirmed || session < 3 ? 'disabledButton' : ''"
            @click="$emit('confirmStakeholders')"
          >
            {{ $t('common.publish') }}
          </el-button>
        </div>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    stakeholders: {
      type: Array,
      default: null,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    editDialog: {
      type: Object,
      default: () => ({
        flag: false,
        user: null,
      }),
    },

    isConfirmed: {
      type: Boolean,
      default: false,
    },

    session: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    formattedStakeholders() {
      if (this.stakeholders.length < 1) return [];

      // remove inactive and order by groups
      let data = [];
      ['manager', 'peer', 'direct_report', 'other'].forEach((gr) => {
        const stakeholders = this.stakeholders.filter(
          (el) => el.is_active && el.org_relationship === gr,
        ).map((el, id, arr) => {
          const local = { ...el };
          if (id > 0) {
            local.hide_relationship = true;
          }
          if (id === arr.length - 1) {
            local.addLine = true;
          } else {
            local.addLine = false;
          }
          return local;
        });

        data = [...data, ...stakeholders];
      });
      return data;
    },

    deactivatedStakeholders() {
      if (this.stakeholders.length < 1) return [];
      return this.stakeholders.filter((el) => !el.is_active);
    },
  },

  methods: {
    rowClass({ row }) {
      return row.addLine ? 'lineBreak' : '';
    },

    toggleEdit(user) {
      const newOption = { flag: true, user };
      this.$emit('update:editDialog', newOption);
    },
  },
};
</script>

<style lang="scss" scoped>
  #stakeholdersList {
    .stakeholderTable {
      .nameWrap {
        text-transform: capitalize;
      }
    }

    .clearButton {
      background: transparent;
      color: var(--themeFontColor);
      border-color: var(--themeFontColor);
    }

    .actions {
      margin-top: 18px;
      text-align: center;

      .disabledButton {
        background: #a1a1a1;
        border-color: #a1a1a1;
        opacity: 0.6;
      }
    }
  }
</style>

<style lang="scss">
  #stakeholdersList {
    .stakeholderTable {
      .el-table__row.lineBreak:not(:last-child) td {
        border-bottom: 1px solid #c4c4c4;
      }

      .cell {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 16px;
      }

      .breakoutGroup {
        font-weight: bold;

        .cell {
          padding-left: 30px;
        }
      }

      .el-table__empty-text {
        text-align: center;
        font-style: italic;
        line-height: 150px;
        color: #c4c4c4;
      }
    }
  }
</style>
